import { CardBase } from './CardBase';
import Image from 'next/image';
import {
  motion,
  MotionValue,
  useMotionValue,
  useScroll,
  useTransform,
} from 'framer-motion';
import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Body1, Header2, Header3 } from 'structure/home/components/Typography';
import { getStaticAssetUrl } from 'utils/url';

interface Props {
  anchorId: string;
}

export const StackedCard = ({ anchorId }: Props) => {
  const container = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ['start start', 'end start'],
  });

  return (
    <div id={anchorId} ref={container} className='relative flex flex-col gap-6'>
      <Header2 className='sticky top-8 z-10 text-center'>
        Building the Future of DeFAi
      </Header2>

      <CardAnimation
        className='top-[110px]'
        targetScale={0.9}
        range={[0.0, 0.9]}
        progress={scrollYProgress}
      >
        <Card
          className='bg-[#000000]'
          title='AI-Centric DeFi Platform'
          img={getStaticAssetUrl(
            '/sfi/static/homepage/ai-centric-deFi-platform.png'
          )}
          description='SFI harnesses AI to streamline DeFi, empowering users and startups in the agentic economy with innovative financial tools.'
        />
      </CardAnimation>
      <CardAnimation
        className='top-[130px]'
        targetScale={0.92}
        range={[0.2, 0.9]}
        progress={scrollYProgress}
      >
        <Card
          className='bg-[#000000]'
          title='Singularity Finance, the Defi Pod for ASI Chain'
          description='SFI will roll out an ”SFI Trading” suite of AI-driven DeFi agents and  tooling to jump-start ASI Chain Pod liquidity, capture fee revenue, and boost SFI token demand'
          img={getStaticAssetUrl(
            '/sfi/static/homepage/agentic-portfolio-management.png'
          )}
        />
      </CardAnimation>
      <CardAnimation
        className='top-[150px]'
        targetScale={0.94}
        range={[0.4, 0.9]}
        progress={scrollYProgress}
      >
        <Card
          className='bg-[#000000]'
          title='Agentic Discovery Hub'
          description='Discover AI-agent protocols with real-time KPIs like TVL and ROI. SFI’s hub enables startups to gain visibility while offering users one-click DeFi actions—staking, farming, bridging, and bonding—for seamless engagement.'
          img={getStaticAssetUrl(
            '/sfi/static/homepage/agentic-discovery-hub.png'
          )}
        />
      </CardAnimation>
      <CardAnimation
        className='top-[150px]'
        targetScale={0.96}
        range={[0.4, 0.9]}
        progress={scrollYProgress}
      >
        <Card
          className='flex bg-[#000000]'
          title='Autonomous AI Trading'
          description='SFI’s AI trading agents execute non-custodial trades on DEXs. Evolving from signal-based to macro-aware strategies, they empower retail investors to thrive in volatile crypto markets effortlessly.'
          img={getStaticAssetUrl(
            '/sfi/static/homepage/autonomous-ai-trading.png'
          )}
        />
      </CardAnimation>
    </div>
  );
};

interface CardProps {
  title: string;
  description: string;
  className?: string;
  img?: string;
}

const Card = (props: CardProps) => {
  const { title, className, description, img } = props;

  return (
    <CardBase
      className={twMerge(
        `relative z-10 border-b  border-r border-positive-900 shadow-[1px_1px_10px_0px_#241f1f]`,
        className
      )}
    >
      <div className='flex flex-col gap-4'>
        <Header3 className='text-softYellow'>{title}</Header3>
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-5'>
          <Body1 as='p' className='col-span-3'>
            {description}
          </Body1>
          <div className='col-span-2 flex justify-center'>
            <Image
              // className='h-[308px] w-[415px]'
              src={img ?? ''}
              alt='graph'
              width={415}
              height={308}
            />
          </div>
        </div>
      </div>
    </CardBase>
  );
};

interface CardAnimationProps {
  className?: string;
  children: React.ReactNode;
  targetScale?: number;
  range?: number[];
  progress?: MotionValue<number>;
}

export const CardAnimation = ({
  className,
  children,
  targetScale = 1.1,
  range = [0, 100],
  progress,
}: CardAnimationProps) => {
  const value = useMotionValue(1);
  const scale = useTransform(progress ?? value, range, [1, targetScale]);

  return (
    <motion.div
      className={twMerge('sticky z-10', className)}
      style={{ scale }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      {children}
    </motion.div>
  );
};
